// Shared utility functions from CreateOptions
import { DRIVE_TAB } from "./constants/tabConstants";
import { stripExtension } from "./ui/uiFunctions";
import {
  createUploadEngine,
  addFileToUploadEngine,
  resumableFiles,
  clearPendingUploadedChunks,
} from "@/components/Upload/index";
import { toast } from "@/components";
// This file contains all the logic from CreateOptions so it can be reused

export const getMediaDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const isVideo = file.type.startsWith("video/");
    const isAudio = file.type.startsWith("audio/");

    if (isAudio) {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
      return;
    }

    if (isVideo) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        const w = video.videoWidth;
        const h = video.videoHeight;
        URL.revokeObjectURL(url);
        resolve({ width: w, height: h });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 0, height: 0 });
      };
      video.src = url;
    } else {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        URL.revokeObjectURL(url);
        resolve({ width: w, height: h });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 0, height: 0 });
      };
      img.src = url;
    }
  });
};

// Returns aspect_ratio (rounded to 2 decimal places) and orientation:
//   1 = landscape, 2 = portrait, 3 = square
export const computeAspectRatioAndOrientation = (
  width: number,
  height: number
): { aspect_ratio: number; orientation: number } => {
  if (!height) return { aspect_ratio: 0, orientation: 1 };
  const ratio = Math.round((width / height) * 100) / 100;
  let orientation: number;
  if (Math.abs(ratio - 1) < 0.05) {
    orientation = 3; // square
  } else if (ratio > 1) {
    orientation = 1; // landscape
  } else {
    orientation = 2; // portrait
  }
  return { aspect_ratio: ratio, orientation };
};

export const createOptionsFileEvent = (
  event: string,
  data: any,
  cb: any,
  callbacks: {
    showUploadProgress: () => void;
    callRegisterMediaOnCms: (file: any, callback: any) => void;
    updateIsFilter: (value: boolean) => void;
    updateIsClearFilter: (value: boolean) => void;
    emptyMediaDetails: () => void;
    emptyAssetDetails: () => void;
    getMedia: (params: any) => void;
    updateFileProgress: (data: any) => void;
    decreaseCounter: () => void;
    setUploadedSuccessfully: (data: any) => void;
    setSoftRefresh: (value: boolean) => void;
    setUploadingError: (data: any) => void;
    openUpload?: () => void;
  }
): any => {
  const {
    showUploadProgress,
    callRegisterMediaOnCms,
    updateIsFilter,
    updateIsClearFilter,
    emptyMediaDetails,
    emptyAssetDetails,
    getMedia,
    updateFileProgress,
    decreaseCounter,
    setUploadedSuccessfully,
    setSoftRefresh,
    setUploadingError,
    openUpload,
  } = callbacks;

  showUploadProgress();
  if (event === "fileAdded") {
    const file = data.file;
    // file.file is the native File object inside the Resumable file wrapper
    const nativeFile: File | undefined = file.file;

    getMediaDimensions(nativeFile ?? (file as unknown as File)).then(
      ({ width, height }) => {
        const { aspect_ratio, orientation } = computeAspectRatioAndOrientation(
          width,
          height
        );

        const uploadFile = {
          ...file,
          isShorts: data.isShorts,
          isCampaign: data.id == "campaign",
          type: "media",
          name: "media",
          asset_type: data.asset_type,
          id: data.id,
          aspect_ratio,
          orientation,
        };

        callRegisterMediaOnCms(uploadFile, (response: any) => {
          updateIsFilter(false);
          updateIsClearFilter(true);

          if (response && response.status === 200) {
            emptyMediaDetails();
            emptyAssetDetails();

            getMedia({ id: response.data.id });
            const id = response.data.id;
            const token = response.data.jwt;
            const query = {
              entity: id,
              key: "media",
              method: "asset.file",
            };

            if (openUpload) {
              openUpload();
            }
            if (cb) {
              cb(query, token);
            }
          }
        });
      }
    );
  } else if (event === "fileProgress") {
    const file = data.file;
    const progress = Math.round(file.progress(false) * 1000) / 10;
    updateFileProgress({
      identifier: file.uniqueIdentifier,
      progress: progress,
    });
  } else if (event === "fileSuccess") {
    decreaseCounter();
    setUploadedSuccessfully({
      identifier: data.id,
      flag: true,
    });
    setSoftRefresh(true);
  } else if (event === "fileError") {
    decreaseCounter();
    // The Uploader guarantees error is a non-empty, user-safe string.
    const { file, error } = data;
    setUploadingError({
      uniqueIdentifier: file.uniqueIdentifier,
      error: error,
    });
  }
};

// Runs the same "replace this media's file" flow used by RenderAction's
// reupload button: media.reupload -> (resume chunk info if it's genuinely
// the same file) -> upload engine wiring. Extracted so any flow holding a
// MediaModel + a File (e.g. the media.register unique-constraint-conflict
// recovery flow) can trigger a reupload without duplicating the engine wiring.
export const runMediaReupload = async (
  media: {
    id: string;
    damid: string;
    team?: string;
    agency?: string;
    asset_type?: string;
    is_short?: number;
    fid?: string;
  },
  file: File,
  callbacks: {
    reuploadMedia: (params: any) => Promise<any>;
    getMedia: (params: any) => Promise<any>;
    getChunkInfo: (params: any) => Promise<any>;
    addFile: (data: any) => void;
    increaseCounter: () => void;
    decreaseCounter: () => void;
    showUploadProgress: () => void;
    updateFileProgress: (data: any) => void;
    setUploadedSuccessfully: (data: any) => void;
    setUploadingError: (data: any) => void;
    onReuploadStart?: () => void;
    onReuploadSettled?: () => void;
    onMediaRefetched?: (media: any) => void;
  }
): Promise<boolean> => {
  const {
    reuploadMedia,
    getMedia,
    getChunkInfo,
    addFile,
    increaseCounter,
    decreaseCounter,
    showUploadProgress,
    updateFileProgress,
    setUploadedSuccessfully,
    setUploadingError,
    onReuploadStart,
    onReuploadSettled,
    onMediaRefetched,
  } = callbacks;

  onReuploadStart?.();

  const response = await reuploadMedia({
    id: media.id,
    damid: media.damid,
    team: media.team,
    agency: media.agency,
    asset_type: media.asset_type,
    is_short: media.is_short,
    title: stripExtension(file.name),
  });

  if (!response || response.status !== 200 || !response.data) {
    onReuploadSettled?.();
    return false;
  }

  // skipStoreUpdate avoids resetting the shared mediaDetails store slice (and
  // whatever is currently rendered from it, e.g. the media detail page) -
  // callers get the fresh data via onMediaRefetched instead.
  const mediaResponse = await getMedia({ id: media.id, skipStoreUpdate: true });
  onMediaRefetched?.(mediaResponse?.data ?? media);

  const token = response.data.jwt;
  const fid = `${file.size}${file.name}`;
  const identifier = `${file.size}-${file.name.replace(/[.\s]/g, "")}`;

  // Only resume from the server's chunk list when this is genuinely the
  // same file the server already has partial chunks for (same size+name
  // fid). A different fid means a different file entirely.
  const isSameFile = mediaResponse?.data?.fid === fid;
  const uploadedChunks = isSameFile
    ? (
        await getChunkInfo({
          upload_id: media.damid,
          token,
        })
      ).data
    : [];

  addFile({
    key: identifier,
    file: {
      id: media.id,
      name: file.name,
      size: file.size,
      asset_type: media.asset_type,
      identifier,
      title: file.name,
      filename: file.name,
      at: "video",
      slikeId: media.id,
      progress: 0,
      uploadPhase: "uploading",
      isUploadedSuccessfully: false,
      resumableIdentifier: identifier,
      error: null,
    },
  });
  increaseCounter();
  showUploadProgress();

  const r = createUploadEngine({
    query: {
      method: "asset.file",
      entity: media.id,
      key: "media",
      upload_id: media.damid,
      resumableReset: !isSameFile,
    },
    headers: { token },
    // Reuploads send one chunk at a time instead of the default 3-in-parallel.
    simultaneousUploads: 1,
  });

  r.on("fileAdded", (rf: any) => {
    resumableFiles[rf.uniqueIdentifier] = rf;

    r.on("fileProgress", (f: any) => {
      updateFileProgress({
        identifier,
        progress: (f.progress(false) * 100) | 0,
      });
    });

    r.on("fileSuccess", () => {
      onReuploadSettled?.();
      decreaseCounter();
      setUploadedSuccessfully({ identifier, flag: true });
      clearPendingUploadedChunks(identifier);
      toast.success("Reupload started successfully");
    });

    r.on("fileError", () => {
      onReuploadSettled?.();
      decreaseCounter();
      setUploadingError({
        uniqueIdentifier: identifier,
        error: "Error while uploading",
      });
      clearPendingUploadedChunks(identifier);
      toast.fail("Error while reuploading media");
    });

    r.upload();
  });

  addFileToUploadEngine(r, file, identifier, uploadedChunks);

  return true;
};

export const createOptionsHandleClick = (
  option: string,
  callbacks: {
    setIsModalOpen: (value: boolean) => void;
    setModalTitle: (title: string) => void;
    setModalContent: (content: any) => void;
    handleCloseModal: () => void;
    closeModal: () => void;
    openUpload: () => void;
    updateActiveCustomUploadTab: (tab: string) => void;
    activeCustomUploadTab: string;
  }
): void => {
  const { setIsModalOpen, setModalTitle } = callbacks;

  if (option === "EMBED") {
    setIsModalOpen(true);
    setModalTitle("CREATE > Embed");
    // You'll need to import EmbedForm here
    // setModalContent(<EmbedForm closeModal={handleCloseModal} />);
  } else if (option === "CUSTOM_UPLOADS") {
    setIsModalOpen(true);
    setModalTitle("CREATE > Custom Uploads");
    // You'll need to import CustomUploadForm here
    // setModalContent(<CustomUploadForm closeModal={handleCloseModal} />);
  } else if (option === "SIMULIVE") {
    setIsModalOpen(true);
    setModalTitle("CREATE > Simulive");
    // You'll need to import SimuliveForm here
    // setModalContent(<SimuliveForm closeModal={handleCloseModal} />);
  }
};

// Export the createOptions array so it can be reused
// Note: Icons need to be imported in the component that uses this
export const createOptions = [
  {
    type: "uploader",
    id: "video",
    datatype: "VIDEO",
    title: "Video",
    description: "Upload video content",
    asset_type: "video",
  },
  {
    type: "uploader",
    id: "audio",
    datatype: "AUDIO",
    title: "Audio",
    description: "Upload audio and podcasts",
    asset_type: "audio",
  },
  {
    type: "uploader",
    id: "shorts",
    datatype: "VIDEO",
    title: "Shorts",
    description: "Upload Shorts/Reels videos",
    asset_type: "shorts",
  },
  {
    type: "uploader",
    id: DRIVE_TAB,
    datatype: "VIDEO",
    title: "Drive",
    description: "Upload to drive",
    asset_type: DRIVE_TAB,
    content_type: 2,
  },
  {
    type: "uploader",
    id: "slates",
    datatype: "VIDEO",
    title: "Slate",
    description: "Upload a slate",
    asset_type: "slates",
  },
  {
    type: "uploader",
    id: "campaign",
    datatype: "VIDEO",
    title: "Campaign",
    description: "Upload campaign video",
    asset_type: "campaign",
  },
  {
    type: "action",
    id: "CUSTOM_UPLOADS",
    title: "Custom Upload",
    description: "Upload via FTP, GDrive...",
    asset_type: "custom",
  },
  {
    type: "action",
    id: "EMBED",
    title: "Embed",
    description: "Embed content",
    asset_type: "embed",
  },
];
